import React, { useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload, X, Image as ImageIcon, AlertTriangle, Check, Info, Eye, EyeOff, Download, ChevronDown, Crop, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import ImageCropperModal from './ImageCropperModal';
import { validateImage, IMAGE_SPECS, formatFileSize } from '../utils/imageValidation';

const API = '/api';

// Safe Zone Overlay Component
const SafeZoneOverlay = () => (
  <div 
    className="absolute inset-0 pointer-events-none flex items-center justify-center"
    data-testid="safe-zone-overlay"
  >
    <div className="absolute inset-0 bg-black/40" />
    <div 
      className="relative bg-transparent border-2 border-dashed border-white/80 flex items-center justify-center"
      style={{ width: '60%', height: '60%' }}
    >
      <div className="absolute inset-0 bg-black/40" style={{ mixBlendMode: 'destination-out' }} />
      <span className="absolute top-2 left-1/2 -translate-x-1/2 text-xs font-medium text-white/90 bg-black/50 px-2 py-0.5 rounded">
        SAFE ZONE
      </span>
      <span className="text-white/70 text-xs text-center px-4">
        Keep titles &amp; faces inside this area
      </span>
    </div>
  </div>
);

const HeroBannerUploader = ({ 
  contentType, // 'series' or 'novel'
  contentId,
  currentBannerUrl,
  onBannerUpdate,
  lastUpdated
}) => {
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(currentBannerUrl);
  const [pendingUrl, setPendingUrl] = useState(null);
  const [validationWarning, setValidationWarning] = useState(null);
  const [validationError, setValidationError] = useState(null);
  const [showSafeZone, setShowSafeZone] = useState(false);
  const [showTemplateDropdown, setShowTemplateDropdown] = useState(false);
  const [showCropper, setShowCropper] = useState(false);
  const [fileToProcess, setFileToProcess] = useState(null);
  const fileInputRef = useRef(null);
  const dropdownRef = useRef(null);

  const spec = IMAGE_SPECS.HERO_BANNER;

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowTemplateDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reset states
    setValidationError(null);
    setValidationWarning(null);
    setPendingUrl(null);

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload a JPEG, PNG, or WebP image');
      return;
    }

    // Use the new forgiving validation
    const validation = await validateImage(file, 'HERO_BANNER');
    
    if (!validation.canUpload) {
      // Only hard errors block upload
      setValidationError(validation.friendlyMessage || validation.error);
      return;
    }
    
    // Show warning if there is one (but don't block)
    if (validation.warning) {
      setValidationWarning(validation.friendlyMessage || validation.warning);
    }

    // If needs cropping, open the cropper
    if (validation.needsCrop) {
      setFileToProcess(file);
      setShowCropper(true);
      return;
    }

    // Image is good - proceed with upload directly
    await uploadFile(file);
  };

  // Handle cropped file from the modal
  const handleCroppedFile = async (croppedFile) => {
    setShowCropper(false);
    setFileToProcess(null);
    setValidationWarning(null);
    await uploadFile(croppedFile);
  };

  // Upload the file (either original or cropped)
  const uploadFile = async (file) => {
    setPreview(URL.createObjectURL(file));
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/upload/hero-banner`, formData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.data.url) {
        setPendingUrl(response.data.url);
        setPreview(response.data.url);
        toast.success('Banner uploaded! Click "Save Banner" to apply.');
      }
    } catch (error) {
      console.error('Upload error:', error);
      const detail = error.response?.data?.detail || 'Failed to upload banner';
      toast.error(detail);
      setValidationError(detail);
      setPreview(currentBannerUrl);
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!pendingUrl) return;

    setSaving(true);
    try {
      const endpoint = contentType === 'series' 
        ? `${API}/series/${contentId}/hero-banner`
        : `${API}/novels/${contentId}/hero-banner`;

      const response = await axios.put(endpoint, 
        { hero_banner_url: pendingUrl },
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
      );

      if (response.data.success) {
        toast.success('Hero banner saved successfully!');
        setPendingUrl(null);
        setValidationWarning(null);
        if (onBannerUpdate) {
          onBannerUpdate(pendingUrl);
        }
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error(error.response?.data?.detail || 'Failed to save banner');
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    if (!window.confirm('Remove the hero banner? This cannot be undone.')) return;

    setSaving(true);
    try {
      const endpoint = contentType === 'series' 
        ? `${API}/series/${contentId}/hero-banner`
        : `${API}/novels/${contentId}/hero-banner`;

      const response = await axios.put(endpoint, 
        { hero_banner_url: null },
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
      );

      if (response.data.success) {
        toast.success('Hero banner removed');
        setPreview(null);
        setPendingUrl(null);
        setValidationWarning(null);
        if (onBannerUpdate) {
          onBannerUpdate(null);
        }
      }
    } catch (error) {
      console.error('Remove error:', error);
      toast.error('Failed to remove banner');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setPreview(currentBannerUrl);
    setPendingUrl(null);
    setValidationError(null);
    setValidationWarning(null);
  };

  return (
    <>
      <Card className="bg-zinc-900 border-zinc-800" data-testid="hero-banner-uploader">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-white flex items-center gap-2">
                <ImageIcon className="w-5 h-5 text-orange-400" />
                Hero Banner (Homepage)
              </CardTitle>
              <CardDescription className="mt-1">
                Wide landscape banner for the homepage hero carousel.
              </CardDescription>
            </div>
            {currentBannerUrl && !pendingUrl && (
              <Badge variant="secondary" className="bg-green-500/20 text-green-400">
                <Check className="w-3 h-3 mr-1" />
                Banner Set
              </Badge>
            )}
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Friendly Guidelines */}
          <div className="flex items-start gap-2 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
            <Sparkles className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-300">
              <p className="font-medium mb-1">Upload any wide landscape image — we'll make it fit!</p>
              <ul className="text-blue-400 space-y-0.5 text-xs">
                <li>• Accepts wide images from 2:1 to 3:1 aspect ratios</li>
                <li>• We'll auto-crop images that need minor adjustments</li>
                <li>• Recommended: {spec.recWidth}×{spec.recHeight}px • Max: {formatFileSize(spec.maxFileSize)}</li>
              </ul>
            </div>
          </div>

          {/* Template Download & Safe Zone Toggle */}
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative" ref={dropdownRef}>
              <Button
                variant="outline"
                size="sm"
                className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                onClick={() => setShowTemplateDropdown(!showTemplateDropdown)}
                data-testid="download-template-btn"
              >
                <Download className="w-4 h-4 mr-1" />
                Download Template
                <ChevronDown className={`w-4 h-4 ml-1 transition-transform ${showTemplateDropdown ? 'rotate-180' : ''}`} />
              </Button>
              
              {showTemplateDropdown && (
                <div className="absolute top-full left-0 mt-1 w-56 bg-zinc-800 border border-zinc-700 rounded-lg shadow-xl z-50 overflow-hidden">
                  <a
                    href="/templates/hero-banner-2560x1100.png"
                    download="hero-banner-template-2560x1100.png"
                    className="flex items-center gap-2 px-3 py-2 text-sm text-zinc-300 hover:bg-zinc-700 transition-colors"
                    onClick={() => setShowTemplateDropdown(false)}
                    data-testid="download-template-2560"
                  >
                    <ImageIcon className="w-4 h-4 text-orange-400" />
                    <div>
                      <div className="font-medium">2560×1100</div>
                      <div className="text-xs text-zinc-500">Recommended</div>
                    </div>
                  </a>
                  <a
                    href="/templates/hero-banner-1920x820.png"
                    download="hero-banner-template-1920x820.png"
                    className="flex items-center gap-2 px-3 py-2 text-sm text-zinc-300 hover:bg-zinc-700 transition-colors border-t border-zinc-700"
                    onClick={() => setShowTemplateDropdown(false)}
                    data-testid="download-template-1920"
                  >
                    <ImageIcon className="w-4 h-4 text-zinc-400" />
                    <div>
                      <div className="font-medium">1920×820</div>
                      <div className="text-xs text-zinc-500">Minimum</div>
                    </div>
                  </a>
                </div>
              )}
            </div>

            {preview && (
              <Button
                variant="outline"
                size="sm"
                className={`border-zinc-700 ${showSafeZone ? 'bg-orange-500/20 text-orange-400 border-orange-500/50' : 'text-zinc-300 hover:bg-zinc-800'}`}
                onClick={() => setShowSafeZone(!showSafeZone)}
                data-testid="toggle-safe-zone-btn"
              >
                {showSafeZone ? <EyeOff className="w-4 h-4 mr-1" /> : <Eye className="w-4 h-4 mr-1" />}
                {showSafeZone ? 'Hide Safe Zone' : 'Show Safe Zone'}
              </Button>
            )}
          </div>

          {/* Soft Warning (non-blocking) */}
          {validationWarning && !validationError && (
            <div className="flex items-start gap-2 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg" data-testid="banner-validation-warning">
              <Info className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-300">{validationWarning}</div>
            </div>
          )}

          {/* Hard Error (blocking) */}
          {validationError && (
            <div className="flex items-start gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg" data-testid="validation-error">
              <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-red-300">{validationError}</div>
            </div>
          )}

          {/* Preview / Upload Area */}
          <div className="space-y-3">
            {preview ? (
              <div className="relative group">
                <div className="relative aspect-[21/9] bg-zinc-800 rounded-lg overflow-hidden">
                  <img
                    src={preview}
                    alt="Hero banner preview"
                    className="w-full h-full object-cover"
                    data-testid="hero-banner-preview-image"
                  />
                  
                  {showSafeZone && <SafeZoneOverlay />}
                  
                  {!showSafeZone && (
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          ref={fileInputRef}
                          accept="image/jpeg,image/png,image/webp"
                          onChange={handleFileSelect}
                          className="sr-only"
                          disabled={uploading || saving}
                        />
                        <Button size="sm" variant="secondary" asChild disabled={uploading}>
                          <span>
                            <Upload className="w-4 h-4 mr-1" />
                            Replace
                          </span>
                        </Button>
                      </label>
                      {currentBannerUrl && !pendingUrl && (
                        <Button 
                          size="sm" 
                          variant="destructive" 
                          onClick={handleRemove}
                          disabled={saving}
                          data-testid="remove-hero-banner-btn"
                        >
                          <X className="w-4 h-4 mr-1" />
                          Remove
                        </Button>
                      )}
                    </div>
                  )}
                </div>
                
                {pendingUrl && (
                  <div className="absolute top-2 right-2">
                    <Badge variant="secondary" className="bg-yellow-500/20 text-yellow-400">
                      Unsaved
                    </Badge>
                  </div>
                )}
              </div>
            ) : (
              <label className="cursor-pointer block">
                <input
                  type="file"
                  ref={fileInputRef}
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleFileSelect}
                  className="sr-only"
                  disabled={uploading}
                  data-testid="hero-banner-file-input"
                />
                <div className={`
                  aspect-[21/9] border-2 border-dashed border-zinc-600 rounded-lg
                  hover:border-orange-500 transition-colors
                  flex flex-col items-center justify-center gap-3
                  ${uploading ? 'opacity-50' : ''}
                `}>
                  {uploading ? (
                    <>
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
                      <span className="text-zinc-400 text-sm">Uploading...</span>
                    </>
                  ) : (
                    <>
                      <Upload className="w-10 h-10 text-zinc-500" />
                      <div className="text-center">
                        <p className="text-zinc-300 font-medium">Click to upload hero banner</p>
                        <p className="text-zinc-500 text-sm">Any wide landscape image works!</p>
                      </div>
                    </>
                  )}
                </div>
              </label>
            )}
          </div>

          {/* Action Buttons */}
          {pendingUrl && (
            <div className="flex items-center gap-2 pt-2">
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-orange-600 hover:bg-orange-700"
                data-testid="save-hero-banner-btn"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Save Banner
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                onClick={handleCancel}
                disabled={saving}
                className="border-zinc-700"
                data-testid="cancel-hero-banner-btn"
              >
                Cancel
              </Button>
            </div>
          )}

          {/* Last Updated */}
          {lastUpdated && !pendingUrl && (
            <p className="text-xs text-zinc-500">
              Last updated: {new Date(lastUpdated).toLocaleDateString()}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Image Cropper Modal */}
      <ImageCropperModal
        isOpen={showCropper}
        onClose={() => {
          setShowCropper(false);
          setFileToProcess(null);
        }}
        file={fileToProcess}
        aspect={spec.targetAspect}
        outputWidth={spec.recWidth}
        outputHeight={spec.recHeight}
        recommendedMin={{ width: spec.minWidth, height: spec.minHeight }}
        imageType="Hero Banner"
        onCropped={handleCroppedFile}
        maxBytes={spec.maxFileSize}
      />
    </>
  );
};

export default HeroBannerUploader;
